/*
  Shared Splide carousel driver.
  ------------------------------
  Mounts any `.splide[data-carousel]` element, but only once its slides overflow
  the track — below that the markup stays a plain static row, which is why every
  caller ships working no-JS CSS for the un-mounted state.

  Everything that differs between callers is a data attribute; everything else
  (overflow gating, slide promotion, progress bar, editor lifecycle) lives here.

  Attributes, all optional unless noted:
    data-carousel               marker; required for this script to touch the element
    data-layout                 'fixed' | 'peek' | 'per-page' | 'auto'   (default 'per-page')
    data-card-width             px, layout=fixed
    data-card-width-mobile      px, layout=fixed  (defaults to data-card-width)
    data-per-view-mobile        float, layout=peek   (e.g. 1.2 → 20% of the next card shows)
    data-per-view               float, layout=peek, >=750px
    data-per-page-mobile        int, layout=per-page
    data-per-page               int, layout=per-page, >=750px
    data-gap                    px, >=750px
    data-gap-mobile             px
    data-drag                   'free' | 'step'                 (default 'free')
    data-omit-end               'true' | 'false'                (default 'true')
    data-clamp-drag             'true' | 'false'                (default 'false')
    data-arrows                 'true' | 'false'
    data-pagination             'dots' | 'progress' | 'none'
    data-overflow-tolerance     px slack in the overflow test      (default 1)
    data-dot-label              translated remote-dot aria-label; %INDEX% is replaced
                                with the slide number

  Remote dots: a section that wants the dots somewhere the carousel can't reach —
  up in a header row, say — renders an empty `[data-carousel-dots-slot]` element
  anywhere inside itself, optionally with `data-slides-per-dot`. This script fills
  it; sections with no such element get no remote dots. The slot is left empty when
  there is nothing to page through, so CSS can hide it with `:empty` instead of
  tracking overflow state.

  They follow `data-pagination`: nothing is rendered under 'none', and under 'dots'
  they replace Splide's built-in pagination rather than showing alongside it.
  'progress' pairs the bar with them (mini-doorways puts dots in its header).

  Slides: Shopify wraps every theme block in `.shopify-block`, so those wrappers —
  not the block markup inside them — are promoted to Splide slides.

  Horizontal wheel and trackpad gestures move the track; vertical ones are left to the
  page. See `driveWheel`.

  Mounting is not a one-shot decision. Every un-mounted row is watched with a
  `ResizeObserver`, so one whose track gains its real width after the first scan — a
  column that settles late, a row inside a `<dialog>` that was closed at load — still
  mounts. Rows drop out of the observer once mounted and go back under it on teardown.
*/
(function () {
  if (window.__themeCarousel) return;
  window.__themeCarousel = true;

  function num(el, name, fallback) {
    var raw = parseFloat(el.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  function bool(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (raw === null) return fallback;
    return raw === 'true';
  }

  // Peek layouts size slides by fraction-of-track so a partial next slide shows.
  // Track `padding` can't do this: it is reserved permanently and leaves a gap at
  // the end. Fractional perPage freezes the drag, hence fixedWidth + calc.
  //
  // Only the gaps *between* visible slides come off the track: 2.2 per view shows
  // slide, gap, slide, gap, sliver — ceil(visible) - 1. Floor would be wrong for a
  // whole number of slides. The un-mounted CSS mirrors this via --carousel-peek-gaps.
  function peekWidth(visible, gap) {
    var gaps = Math.max(0, Math.ceil(visible) - 1);
    return 'calc((100% - ' + gaps * gap + 'px) / ' + visible + ')';
  }

  function sizingFor(el, layout, gap, mobile) {
    // Content-width slides: each slide is as wide as what it holds. What the quick
    // links row needs — its cells size to their labels, so neither a fixed width nor
    // a per-page count can describe them.
    if (layout === 'auto') {
      return { autoWidth: true };
    }
    if (layout === 'fixed') {
      var fixed = num(el, 'data-card-width', 220);
      return { fixedWidth: (mobile ? num(el, 'data-card-width-mobile', fixed) : fixed) + 'px' };
    }
    if (layout === 'peek') {
      var view = mobile
        ? num(el, 'data-per-view-mobile', 1.2)
        : num(el, 'data-per-view', 2.2);
      return { fixedWidth: peekWidth(view, gap) };
    }
    var perPage = mobile
      ? num(el, 'data-per-page-mobile', 1)
      : num(el, 'data-per-page', 3);
    return { perPage: perPage };
  }

  // Splide has no built-in progress bar, so drive it from the track position
  // rather than the slide index — that keeps it truthful during a free drag.
  function driveProgressBar(splide, el) {
    var bar = el.querySelector('.splide__progress__bar');
    if (!bar) return;

    function rateFor(pos) {
      var move = splide.Components.Move;
      var start = move.getLimit(false);
      var end = move.getLimit(true);
      if (end === start) return 1;
      return Math.min(Math.max((pos - start) / (end - start), 0), 1);
    }

    function setBar(rate) {
      bar.style.width = 100 * rate + '%';
    }

    // Arrow/programmatic moves animate to the destination.
    splide.on('move', function (index, prev, dest) {
      setBar(rateFor(splide.Components.Move.toPosition(dest)));
    });
    // Live position while dragging/scrolling.
    splide.on('mounted dragging scrolled moved updated resized', function () {
      setBar(rateFor(splide.Components.Move.getPosition()));
    });
    // Follow the finger 1:1 while dragging (no easing lag).
    splide.on('drag', function () { bar.style.transition = 'none'; });
    splide.on('dragged', function () { bar.style.transition = ''; });
  }

  /* Trackpads and horizontal wheels. Splide's own `wheel` option reads `deltaY`, which
     would take the page's vertical scroll with it, so the gesture is handled here and
     only when it is horizontal — a vertical wheel over a carousel still scrolls the page.

     Free-drag rows follow the gesture 1:1 through `Move.translate` and snap to the nearest
     slide once it stops, which is what the same rows do after a pointer drag. Stepped rows
     have no intermediate position to sit at, so the delta accumulates and pages instead. */
  function driveWheel(splide, el) {
    var Move = splide.Components.Move;
    if (!Move) return;

    var stepped = el.getAttribute('data-drag') === 'step';
    var accumulated = 0;
    var idle = null;

    function clamp(position) {
      var start = Move.getLimit(false);
      var end = Move.getLimit(true);
      return Math.min(Math.max(Math.min(start, end), position), Math.max(start, end));
    }

    // The gesture is over. Free rows land on a slide edge; stepped rows drop the remainder
    // so a slow scroll cannot page twice off one flick.
    function settle() {
      idle = null;
      accumulated = 0;
      if (!stepped) splide.go(Move.toIndex(Move.getPosition()));
    }

    el.addEventListener(
      'wheel',
      function (e) {
        // Vertical intent, or a row with nothing to scroll: leave it to the page. The
        // listener outlives a teardown, so a re-mounted row's own handler owns it now.
        if (el.__splide !== splide) return;
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        if (!el.classList.contains('is-overflow')) return;

        // A line- or page-mode wheel reports 1s and 2s; scale them to something moving.
        var delta = e.deltaX * (e.deltaMode === 0 ? 1 : 16);

        e.preventDefault();
        if (stepped) {
          accumulated += delta;
          if (Math.abs(accumulated) >= 40) {
            splide.go(accumulated > 0 ? '>' : '<');
            accumulated = 0;
          }
        } else {
          Move.translate(clamp(Move.getPosition() - delta));
        }

        // No wheel event says it is the last one, so the end of the gesture is a gap.
        if (idle) clearTimeout(idle);
        idle = setTimeout(settle, 120);
      },
      { passive: false }
    );
  }

  // The slot is section-level but carousels are merchant-placed, so a section can
  // hold more than one. First to mount owns it; the rest keep their own in-carousel
  // pagination rather than fighting over the same element. Returns null when there
  // is no slot, or when another carousel already owns it.
  function claimDotsSlot(el) {
    var root = el.closest('.shopify-section');
    var slot = root && root.querySelector('[data-carousel-dots-slot]');
    if (!slot) return null;
    if (slot.__carouselOwner && slot.__carouselOwner !== el) return null;
    slot.__carouselOwner = el;
    return slot;
  }

  // Dots rendered outside the carousel (e.g. up in a section header). One dot per
  // `perDot` slides, so a drag that advances a couple of slides doesn't skip a dot.
  function driveRemoteDots(splide, el, slot) {
    var perDot = Math.max(1, num(slot, 'data-slides-per-dot', 1));
    // Translated by Liquid, with the slide number left as a %INDEX% placeholder
    // because this file is a plain asset and cannot call the `t` filter itself.
    var dotLabel = el.getAttribute('data-dot-label') || 'Go to item %INDEX%';
    var dots = [];

    function syncDots() {
      if (!dots.length) return;
      var active = Math.min(Math.floor(splide.index / perDot), dots.length - 1);
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === active);
        dots[i].setAttribute('aria-current', i === active ? 'true' : 'false');
      }
    }

    function buildDots() {
      slot.textContent = '';
      dots = [];

      var end = splide.Components.Controller.getEnd();
      var total = Math.ceil((end + 1) / perDot);
      // A single dot conveys nothing — leaving the slot empty also lets callers
      // hide it with :empty rather than tracking overflow state in CSS.
      if (total < 2) return;

      var list = document.createElement('ul');
      list.className = 'carousel__dots';

      for (var i = 0; i < total; i++) {
        (function (index) {
          var item = document.createElement('li');
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute('aria-label', dotLabel.replace('%INDEX%', index * perDot + 1));
          dot.addEventListener('click', function () {
            splide.go(Math.min(index * perDot, splide.Components.Controller.getEnd()));
          });
          item.appendChild(dot);
          list.appendChild(item);
          dots.push(dot);
        })(i);
      }

      slot.appendChild(list);
      syncDots();
    }

    splide.on('mounted updated resized', buildDots);
    splide.on('move moved scrolled dragged', syncDots);
  }

  function build(el) {
    var layout = el.getAttribute('data-layout') || 'per-page';
    var gap = num(el, 'data-gap', 0);
    var gapMobile = num(el, 'data-gap-mobile', gap);
    var pagination = el.getAttribute('data-pagination') || 'none';

    // Theme blocks wrap each item in .shopify-block. Promote those wrappers to
    // slides so the sizing config applies to the list's direct children.
    //
    // A card block that renders nothing — unlinked, or with every field empty —
    // still leaves its wrapper behind, so skip the empty ones: promoting them
    // would put blank slides in the track and skew paging and drag. The matching
    // `:not(:has(*))` rule in _carousel.liquid keeps them out of the static row.
    var list = el.querySelector('.splide__list');
    if (list) {
      for (var i = 0; i < list.children.length; i++) {
        if (list.children[i].firstElementChild) {
          list.children[i].classList.add('splide__slide');
        }
      }
    }

    // Remote dots follow the pagination setting: 'none' means no dots anywhere,
    // and under 'dots' they stand in for Splide's built-in pagination instead of
    // doubling up with it. 'progress' can still pair with them — mini-doorways
    // shows dots in its header and the bar below the cards.
    var dotsSlot = pagination === 'none' ? null : claimDotsSlot(el);

    // mediaQuery 'min' → the base config is mobile, the 750 breakpoint is desktop.
    var base = sizingFor(el, layout, gapMobile, true);
    var desktop = sizingFor(el, layout, gap, false);

    var options = {
      type: 'slide',
      mediaQuery: 'min',
      speed: 400,
      perMove: 1,
      omitEnd: bool(el, 'data-omit-end', true),
      arrows: bool(el, 'data-arrows', false),
      pagination: pagination === 'dots' && !dotsSlot,
      gap: gapMobile + 'px',
      breakpoints: { 750: { gap: gap + 'px' } }
    };

    if (el.getAttribute('data-drag') === 'step') {
      options.drag = true;
    } else {
      options.drag = 'free';
      options.snap = true;
    }

    for (var key in base) options[key] = base[key];
    for (var dkey in desktop) options.breakpoints[750][dkey] = desktop[dkey];

    var splide = new Splide(el, options);

    if (pagination === 'progress') driveProgressBar(splide, el);
    if (dotsSlot) driveRemoteDots(splide, el, dotsSlot);

    // Controls are gated on `.is-overflow` in CSS. Splide maintains the class
    // itself, but callers also read it, so keep it authoritative from mount on.
    var reportedOverflow = false;
    splide.on('overflow', function (isOverflow) {
      reportedOverflow = true;
      el.classList.toggle('is-overflow', !!isOverflow);
    });

    splide.mount();
    // After mount — `Components` is empty until then.
    if (bool(el, 'data-clamp-drag', false)) clampDrag(splide);
    driveWheel(splide, el);
    // Splide does not always emit `overflow` for the initial state. Only fill that
    // gap — we mount on our own overflow test, so the class is right by then. Never
    // unconditionally: mount() may have emitted `overflow` with false (our test and
    // Splide's disagree near the threshold), and re-adding would strand the arrows
    // and progress bar on a track Splide has decided no longer overflows.
    if (!reportedOverflow) el.classList.add('is-overflow');
    el.__splide = splide;
  }

  // Splide lets the track be pulled past its limits with friction and springs it back on
  // release. Clamping removes the overshoot: Drag and Scroll reach the exposed
  // `Move.translate` property, while Move's own moves use an internal binding and are
  // untouched — so paging and transitions keep their easing and only the drag is bounded.
  function clampDrag(splide) {
    var Move = splide.Components.Move;
    if (!Move || typeof Move.translate !== 'function' || typeof Move.getLimit !== 'function') return;

    var translate = Move.translate;
    Move.translate = function (position, preventLoop) {
      // getLimit already accounts for trimSpace, so the end bound is the flush position.
      // Sign flips with direction, so derive which bound is which rather than assuming.
      var start = Move.getLimit(false);
      var end = Move.getLimit(true);
      return translate.call(
        Move,
        Math.min(Math.max(start, end), Math.max(Math.min(start, end), position)),
        preventLoop
      );
    };
  }

  // Do the slides overflow the visible track? The tolerance absorbs sub-pixel
  // rounding on calc()-based slide widths, so an exact N-up row is not mistaken
  // for overflow. A real extra slide adds far more than a few px.
  function overflows(el) {
    var track = el.querySelector('.splide__track');
    var list = el.querySelector('.splide__list');
    if (!track || !list || !track.clientWidth) return false;
    return list.scrollWidth - track.clientWidth > num(el, 'data-overflow-tolerance', 1);
  }

  function evaluate(el) {
    if (el.classList.contains('is-initialized')) return;
    if (overflows(el)) build(el);
  }

  /* The load scan measures once, and `resize` and `shopify:section:load` are the only other
     triggers — neither fires on an ordinary load. Watching each un-mounted row is what
     catches a track that gets its real width later. */
  var watcher =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) measured(entries[i].target);
        })
      : null;

  // Mounted rows self-manage — Splide has its own observers — so drop them.
  function measured(el) {
    evaluate(el);
    if (el.classList.contains('is-initialized')) unwatch(el);
  }

  function watch(el) {
    if (!watcher || el.__carouselWatched) return;
    el.__carouselWatched = true;
    watcher.observe(el);
  }

  function unwatch(el) {
    if (!watcher || !el.__carouselWatched) return;
    el.__carouselWatched = false;
    watcher.unobserve(el);
  }

  function evaluateAll(root) {
    (root || document).querySelectorAll('.splide[data-carousel]').forEach(function (el) {
      evaluate(el);
      if (el.classList.contains('is-initialized')) unwatch(el);
      else watch(el);
    });
  }

  function ready() {
    if (window.Splide) return evaluateAll();
    var tries = 0;
    var timer = setInterval(function () {
      if (window.Splide || tries++ > 60) {
        clearInterval(timer);
        evaluateAll();
      }
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  // Re-check on resize; mounted carousels self-manage.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { evaluateAll(); }, 200);
  });

  /* Splide's own `destroy()` leaves `is-initialized` and `is-overflow` on the element, which
     strands the row between the mounted and static layouts and makes `evaluate` skip it for
     good. Undoing that belongs here, with the rest of the mount contract. */
  function teardown(el) {
    if (el.__splide) {
      el.__splide.destroy();
      el.__splide = null;
    }
    el.classList.remove('is-initialized', 'is-overflow');
    // Or the next mount shows the old fill until its first event lands.
    var bar = el.querySelector('.splide__progress__bar');
    if (bar) bar.style.width = '';
    // Not re-watched: an observer firing mid-swap would mount the row on the placeholders
    // it is about to drop. The `refresh` that ends the swap puts it back under watch.
    unwatch(el);
  }

  /* Callers that own their own DOM lifecycle re-scan through this — the search drawer
     mounts on open, because a closed `<dialog>` measures a zero-width track, and again
     after each swap. `evaluate` skips anything already initialised, so calls are free.
     Anything replacing the slides under a mounted row calls `destroy` first. */
  window.themeCarousel = { refresh: evaluateAll, destroy: teardown };

  // Theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) { evaluateAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var els = e.target.querySelectorAll('.splide[data-carousel]');
    for (var i = 0; i < els.length; i++) teardown(els[i]);
  });
})();
